import { BookingPaymentSource, PaymentSourceType, useFeatureFlag, usePaymentSourceCardInfo } from 'obt-common';
import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getTodayDateTime, isBefore } from 'obt-common/date-utils';
import { useBlocksTheme } from '@spotnana/blocks/src/utils/styling/useBlocksTheme';
import CardBase from './CardBase';
import { CardCaption } from './CardCaption';

interface CardOptionProps {
  /**
   * Details of the payment source
   */
  paymentSource: BookingPaymentSource;
  /**
   * Callback fired on payment source select
   */
  onPaymentSourceSelect?: (paymentSourceId: string) => void;
  /**
   * ID of the selected payment source
   */
  selectedPaymentSourceId?: string;
  hasError?: boolean;
}

const CardOption: FC<CardOptionProps> = ({
  paymentSource,
  onPaymentSourceSelect,
  selectedPaymentSourceId,
  hasError,
}) => {
  const { t: tCommon } = useTranslation('COMMON');
  const { t: tt } = useTranslation('WEB');
  const isDirectBillingMigrationEnabled = useFeatureFlag('FE_DIRECT_BILLING_NEW_UI');
  const { palette } = useBlocksTheme();

  const {
    id,
    cardLogo,
    maskedCardNumber,
    cardTitle,
    cardCompany,
    cardExpireDate,
    isUsable,
    isCentralCard,
    customLogo,
    paymentSourceType,
    budgetBalance,
    isAddOnPolicyViolation,
    disabledCardInfoText,
    cardType,
  } = usePaymentSourceCardInfo(paymentSource);

  const { CustomPaymentMethod, VendorProgramPayment } = PaymentSourceType;
  const [isCardExpired, setIsCardExpired] = useState(false);

  /**
   * This effect determines whether to show expired label
   */
  useEffect(() => {
    const today = getTodayDateTime('MM/YY');
    setIsCardExpired(isBefore(cardExpireDate, today, 'MM/YY'));
  }, [cardExpireDate]);

  return (
    <div>
      {paymentSourceType === VendorProgramPayment && (
        <CardBase
          title={isDirectBillingMigrationEnabled ? cardTitle : cardCompany}
          logo={cardLogo}
          company={cardCompany}
          number={tt('Direct bill')}
          // TODO: Verify that this prop is correct
          expiryDate={maskedCardNumber}
          isCentralCard={isCentralCard}
          onClick={() => {
            if (isUsable) {
              onPaymentSourceSelect?.(paymentSource.id);
            }
          }}
          isSelected={selectedPaymentSourceId === id}
          isDisabled={!isUsable || isCardExpired}
        />
      )}

      {paymentSourceType === CustomPaymentMethod && (
        <CardBase
          title={cardTitle}
          logo={cardLogo}
          company={cardCompany}
          number={budgetBalance ? tt('Available balance') : ''}
          expiryDate={budgetBalance || tt('Limit hidden')}
          customLogo={customLogo}
          isCentralCard={isCentralCard}
          onClick={() => {
            if (isUsable) {
              onPaymentSourceSelect?.(paymentSource.id);
            }
          }}
          isSelected={selectedPaymentSourceId === id}
          hasError={hasError}
          isDisabled={!isUsable || isCardExpired}
        />
      )}

      {![CustomPaymentMethod, VendorProgramPayment].some((type) => type === paymentSourceType) && (
        <CardBase
          title={tCommon(cardTitle)}
          number={maskedCardNumber}
          logo={cardLogo}
          company={cardCompany}
          expiryDate={cardExpireDate}
          customLogo={customLogo}
          isCentralCard={isCentralCard}
          onClick={() => {
            if (isUsable && !isCardExpired) {
              onPaymentSourceSelect?.(paymentSource.id);
            }
          }}
          isSelected={selectedPaymentSourceId === id}
          hasError={hasError}
          isDisabled={!isUsable || isCardExpired || !!isAddOnPolicyViolation}
          disableTooltipText={tCommon(disabledCardInfoText)}
          cardType={cardType}
        />
      )}

      {isCardExpired && <CardCaption value={tt('Expired')} color={palette.text.tertiary} />}
      {!!isAddOnPolicyViolation && <CardCaption value={tt('Not allowed')} color={palette.text.tertiary} />}
    </div>
  );
};

export default CardOption;
